import { CircleX } from 'lucide-react'
import { useId } from 'react'

import { ActionInfo } from './ActionInfo'
import { Button } from './Button'
import type { SelectOption } from './Fields'
import { resourceOptionText } from './resourceOptionText'
import { StatusMessage } from './StatusMessage'

import './resourceSelect.css'

/** Источник в поле выбора: имя — `label`, метка вида («AQSI», «1С») и признак «нет связи» — как на плитке. */
export interface ResourceOption extends SelectOption {
  /** Метка вида, как на плитке источника. */
  mark?: string
  /** Цвет вида источника (палитра `--krp-source-<tone>`), как на плитке. */
  tone?: string
  /** Источник не отвечает — как красная метка «Нет связи» на плитке. */
  down?: boolean
}

/**
 * Источник одной строкой, как шапка плитки: метка вида, имя, красная метка «Нет связи». Одна разметка на поле выбора
 * (выбранный источник под списком) и на заголовки карточек источника, чтобы источник везде выглядел одинаково.
 */
export function ResourceLabel({ name, mark, tone, down = false, id }: { name: string; mark?: string; tone?: string; down?: boolean; id?: string }) {
  return (
    <span id={id} className="krp-resource-select__chosen" {...(tone === undefined ? {} : { 'data-source': tone })} {...(down ? { 'data-down': 'true' } : {})}>
      {mark !== undefined && mark !== '' && (
        <span className="krp-resource-select__mark" aria-hidden="true">
          {mark}
        </span>
      )}
      <strong className="krp-resource-select__name">{name}</strong>
      {down && (
        <span className="krp-resource-select__down">
          <CircleX size={14} aria-hidden="true" />
          <span>Нет связи</span>
        </span>
      )}
    </span>
  )
}

/**
 * Поле «Источник» / «Ресурс» в форме — единый вид выбора источника списком (RP-SRC2); плитками источник выбирают на
 * экранах разделов (`ResourceTile`). Под списком выбранный источник показан так же, как на плитке: метка вида, имя и
 * красная метка «Нет связи» (значок, слово и рамка, цвет только усиливает — CLAUDE.md §7). Пустой список и ошибка
 * загрузки — те же сообщения, что у общего экрана выбора: ошибка дословно, с копированием и кнопкой «Повторить».
 * Компонент знает только про внешний вид: список, ошибку и повтор отдаёт экран.
 */
export function ResourceSelectField({
  label,
  value,
  options,
  onChange,
  disabled = false,
  actionId,
  disabledReason = null,
  notChosen,
  loading = false,
  loadError = null,
  onRetry,
  emptyTitle = 'Нет источников',
  emptyText,
}: {
  label: string
  value: string
  options: readonly ResourceOption[]
  onChange: (value: string) => void
  disabled?: boolean
  actionId?: string
  disabledReason?: string | null
  /** Подпись пункта «не выбрано» (значение — пустая строка); без неё пункта нет. */
  notChosen?: string
  /** Список ещё загружается: вместо пустого списка — «Загружаем…», а не ложное «источников нет». */
  loading?: boolean
  /** Список не загрузился: текст ошибки как пришёл от сервера. */
  loadError?: string | null
  /** Повторить загрузку списка; без неё кнопки нет. */
  onRetry?: () => void
  emptyTitle?: string
  emptyText?: string
}) {
  const id = useId()
  const chosenId = `${id}-chosen`
  const head = (
    <label className="krp-field__label" htmlFor={id}>
      {label}
    </label>
  )
  const info =
    actionId === undefined ? (
      head
    ) : (
      <ActionInfo id={actionId} label={label} disabled={disabled} disabledReason={disabledReason}>
        {head}
      </ActionInfo>
    )
  if (loading || loadError !== null || options.length === 0) {
    return (
      <div className="krp-field krp-resource-select">
        {info}
        {loading ? (
          <StatusMessage kind="loading" title="Загружаем список источников" />
        ) : loadError !== null ? (
          <StatusMessage
            kind="unavailable"
            title="Не удалось загрузить список источников"
            text={loadError}
            {...(onRetry === undefined ? {} : { action: <Button onClick={onRetry}>Повторить</Button> })}
          />
        ) : (
          <StatusMessage kind="empty" title={emptyTitle} {...(emptyText === undefined ? {} : { text: emptyText })} />
        )}
      </div>
    )
  }
  const chosen = options.find((option) => option.value === value)
  const showChosen = chosen !== undefined && ((chosen.mark !== undefined && chosen.mark !== '') || chosen.down === true)
  return (
    <div className="krp-field krp-resource-select">
      {info}
      <select
        id={id}
        value={value}
        disabled={disabled}
        {...(showChosen ? { 'aria-describedby': chosenId } : {})}
        onChange={(event) => onChange(event.target.value)}
      >
        {notChosen !== undefined && <option value="">{notChosen}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {resourceOptionText(option)}
          </option>
        ))}
      </select>
      {showChosen && (
        <ResourceLabel id={chosenId} name={chosen.label} {...(chosen.mark === undefined ? {} : { mark: chosen.mark })} {...(chosen.tone === undefined ? {} : { tone: chosen.tone })} down={chosen.down === true} />
      )}
    </div>
  )
}
